import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FiSearch, FiChevronDown, FiX } from 'react-icons/fi';

// Normaliza acentos para búsqueda case/accent-insensitive.
const normalize = (s) =>
  (s || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

export const filterInstructors = (instructors, query) => {
  const q = normalize(query).trim();
  if (!q) return instructors;
  return instructors.filter((i) => normalize(i.name).includes(q));
};

const Avatar = ({ inst, size = 20 }) => {
  if (inst?.photoURL) {
    return (
      <img
        src={inst.photoURL}
        alt=""
        className="rounded-full object-cover shrink-0"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full flex items-center justify-center text-white font-bold shrink-0"
      style={{
        width: size,
        height: size,
        backgroundColor: inst?.color || '#6366f1',
        fontSize: Math.round(size * 0.45),
      }}
    >
      {(inst?.name || '?')[0]}
    </div>
  );
};

const InstructorSearchSelect = ({
  instructors = [],
  value = '',
  onChange,
  placeholder = 'Elegir...',
  className = '',
  buttonClassName = '',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(
    () => instructors.find((i) => i.id === value) || null,
    [instructors, value]
  );
  const filtered = useMemo(
    () => filterInstructors(instructors, query),
    [instructors, query]
  );

  // Cerrar al clic fuera
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Reset y foco al abrir
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Reset índice activo al cambiar el filtro
  useEffect(() => {
    setActiveIdx(0);
  }, [query]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = filtered[activeIdx];
      if (pick) {
        onChange?.(pick.id);
        setOpen(false);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center gap-2 px-2.5 py-2 border border-gray-200 rounded-xl text-[11px] bg-white hover:border-gray-300 transition shadow-sm ${buttonClassName}`}
      >
        {selected ? (
          <>
            <Avatar inst={selected} size={20} />
            <span className="flex-1 text-left truncate text-gray-800">
              {selected.name}
            </span>
          </>
        ) : (
          <span className="flex-1 text-left text-gray-400">{placeholder}</span>
        )}
        <FiChevronDown size={12} className="text-gray-400 shrink-0" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          <div className="px-2 py-1.5 border-b border-gray-100 flex items-center gap-1.5">
            <FiSearch size={12} className="text-gray-400 shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Buscar instructor…"
              className="flex-1 text-[11px] py-1 outline-none bg-transparent"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX size={12} />
              </button>
            )}
          </div>
          <ul className="max-h-64 overflow-y-auto">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-[11px] text-gray-400">Sin resultados</li>
            ) : (
              filtered.map((inst, idx) => (
                <li key={inst.id}>
                  <button
                    type="button"
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={() => {
                      onChange?.(inst.id);
                      setOpen(false);
                    }}
                    className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-[11px] transition ${
                      idx === activeIdx
                        ? 'bg-indigo-50 text-indigo-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    } ${value === inst.id ? 'font-semibold' : ''}`}
                  >
                    <Avatar inst={inst} size={20} />
                    <span className="flex-1 text-left truncate">{inst.name}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default InstructorSearchSelect;
